import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Optional, ViewChild, WritableSignal, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PaymentResponseDto } from '../shared/models/paymentResponseDto';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { RequestsService } from '../shared/services/requests.service';
import { AuditService } from '../shared/services/audit.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { SnackbarService } from '../shared/services/snackbar.service';
import { BankAccount } from '../shared/models/bankAccount';
import { BankAccountService } from '../shared/services/bank-account.service';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { RequestWorkflowDto } from '../shared/models/request-stage';
import { MatterService } from '../shared/services/matter.service';
import { ReturnReasonDialogComponent } from '../return-reason-dialog/return-reason-dialog.component';
import { Currency } from '../shared/models/currency';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { StagesLabel } from '../enums/stages.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserRole } from '../shared/models/user-role';
import { RpaStatus } from '../enums/rpa-status.enum';
import { Authoriser } from '../shared/models/authoriser';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { applyGeneratedTransactionReference, buildPaymentTransactionReference } from '../shared/utils/transaction-reference';

@Component({
    selector: 'app-view-payment-request',
    templateUrl: './view-payment-request.component.html',
    styleUrl: './view-payment-request.component.scss'
})
export class ViewPaymentRequestComponent implements OnInit, OnDestroy {
    @ViewChild('dialogContent', { read: ElementRef }) dialogContent!: ElementRef;

    private readonly destroy$ = new Subject<void>();
    currentDate: Date = new Date(Date.now())
    RequestType = RequestType;
    paymentRequestForm!: FormGroup;
    canEditFields: boolean = false;
    /** The clients on the request's matter. With more than one, the payee is ticked from them. */
    clientOptions: string[] = [];
    /** The clients ticked in the dropdown. Only the text is part of the form; it is these names joined. */
    readonly payeeClients = new FormControl<string[]>([], { nonNullable: true });
    readonly payeeErrorMatcher: ErrorStateMatcher = {
        isErrorState: () => {
            const payee = this.paymentRequestForm?.get('name');
            return !!payee && payee.touched && payee.invalid;
        }
    };
    accountsEditableFields: boolean = false;
    paymentData!: PaymentResponseDto;
    isInternational!: Boolean;
    showReasonOfPayment!: Boolean;
    typeOfPayment: string = '';
    fileNames: AttachmentData[] = [];
    displayFile: string[] = [];
    disableUpload: Boolean = false;
    disableButtons: Boolean = false;
    paymentType!: boolean;
    bankAccount!: string;
    bankAccounts: BankAccount[] = [];
    purchaseCodes: { code: string, name: string }[] = [];
    filteredBankAccounts: BankAccount[] = [];
    approveBtn: string = "Accept";
    previousWorkflows: RequestWorkflowDto[] = [];
    fileToBeDownloaded!: RequestDocAttachmentResponse;
    isAmountReleased: boolean = false;
    isAmountReleasedDisabled: boolean = false;
    currencyDropdown: Currency[] = [];
    partnerList: Authoriser[] = [];
    isDisabled = false;
    isStageAccounts1 = false;
    isStagePartner2 = false;
    isStagePartner2Return = false;
    showReturnReason = false;
    showFundsCheckedByPartner: boolean = false;
    showFundsCheckedByAccounts: boolean = false;
    FundsCheckedPartnerText: boolean = false;
    FundsCheckedAccountsText: boolean = false;
    isLastAuditRpaSuccessful: boolean = false;
    isApproveBlockedByRpaFailure: boolean = false;
    isRejectionRequested: boolean = false;
    StagesLabel = StagesLabel;
    isDialogOpen: WritableSignal<boolean> = signal(false);
    isPaymentRequestDataLoading: WritableSignal<boolean> = signal(true);
    actionstepBaseUrl: string = '';
    actionstepOverviewBaseUrl: string = '';
    actionstepMatterUrl: string = '';
    actionstepMatterOverviewUrl: string = '';
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);
    matterNo!: number;
    selectedBankAccountId: string = '';
    /** The reference this screen last generated, so a reference the Accounts user typed is never overwritten. */
    private lastGeneratedTransactionReference = '';

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: PaymentResponseDto,
        private formBuilder: FormBuilder,
        private requestService: RequestsService,
        private matterService: MatterService,
        private bankAccountService: BankAccountService,
        private auditService: AuditService,
        private dialog: MatDialog,
        private snackbarService: SnackbarService,
        private router: Router,
        private route: ActivatedRoute,
        private manageUserRoleService: ManageUserRoleService,
        @Optional() public dialogRef?: MatDialogRef<ViewPaymentRequestComponent>
    ) {
        this.isDialogOpen.set(!!this.data);
        this.buildForm();
        if (dialogRef) {
            dialogRef.disableClose = true;
        }
    }

    async ngOnInit(): Promise<void> {
        await this.loadRequestData();
        await this.resolveActionstepLinks();
        await this.loadSupportingData();
        await this.loadClientOptions();
        this.initializeStageFlags();
        this.patchFormValues();
        this.applyFieldStates();
        this.applyStageValidators();
        this.setupValueChangeListeners();
        this.setupTransactionReferenceGeneration();
        this.setupStorageListener();
    }

    /**
     * Fills the Transaction Reference (TTO / FPO plus the day) from the payment method and the Transaction Date,
     * for the Accounts user, who can still change it.
     */
    private setupTransactionReferenceGeneration(): void {
        // Only the Accounts user fills in the reference; at other stages the saved value is shown as it is.
        if ((this.paymentData?.currentStage?.stageOrder ?? 0) !== 4) return;

        const refresh = (): void => {
            const generated = buildPaymentTransactionReference(
                this.paymentRequestForm.get('chapsRequired')?.value === true,
                this.paymentRequestForm.get('dateTransaction')?.value);

            this.lastGeneratedTransactionReference = applyGeneratedTransactionReference(
                this.paymentRequestForm.get('transactionReference'), generated, this.lastGeneratedTransactionReference);
        };

        ['chapsRequired', 'dateTransaction'].forEach(name =>
            this.paymentRequestForm.get(name)?.valueChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe(refresh));

        refresh();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildForm(): void {
        this.paymentRequestForm = this.formBuilder.group({
            returnReason: [''],
            matterNumber: [''],
            matter: [''],
            matterName: [''],
            dateTransaction: [new Date(), Validators.required],
            dateAuthorized: '',
            dateRequested: '',
            requestedTime: [{ value: '', disabled: true }],
            transactionTime: [{ value: '' }],
            reason: ['', Validators.required],
            returnMoniesExplanationIfNo: [''],
            otherInfo: '',
            address: [''],
            amount: ['', [Validators.required, Validators.pattern(/^[0-9.,]*$/)]],
            vatAmount: '',
            currency: '',
            name: ['', Validators.required],
            sortCode: ['', [Validators.required, Validators.maxLength(6), Validators.pattern(/^\d{6}$/)]],
            accountNo: ['', [Validators.required, Validators.maxLength(8), Validators.minLength(8), Validators.pattern(/^\d{8}$/)]],
            bankName: [''],
            reference: ['', Validators.required],
            transactionReference: [''],
            finalBeneficiaryAccNo: '',
            finalBeneficiarySwift: '',
            finalReference: '',
            intermAccountNo: '',
            intermSwift: '',
            intermBankName: '',
            interReference: '',
            isMoniesReturned: [false, Validators.required],
            chapsRequired: [false, Validators.required],
            isPropertyPurchase: false,
            purchaseCode: null,
            partnerAuthorisingId: [null],
            uploadedFiles: [[]],
            clientOffice: '',
            clientBankAccount: ['', Validators.required],
            confirm: [false, Validators.required],
            isCheckedLedger: false,
            isPartnerCertified: [false],
            fundsCheckedByPartner: [false, Validators.requiredTrue],
            fundsCheckedByAccounts: [false, Validators.requiredTrue],
            isAccountDetailsVerifies: ['', Validators.required],
            telephoneVerificationDate: [null],
            verificationSpokeTo: [''],
            verificationNoReason: [''],
            matterPartner: [null],
            nonPartnerReason: [''],
            comments: '',
            confirmPayeeName: [null],
            noMatchReason: [''],
            partnerResponse: [''],
            fileUpload: new FormControl(null, this.isFileUploadRequired ? [Validators.required] : [])
        });
    }

    private async loadRequestData(): Promise<void> {
        if (this.data) {
            this.paymentData = this.data;
            this.isPaymentRequestDataLoading.set(false);
            await this.resolveCreatorPartnerApprove();
            return;
        }

        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) {
            console.error('No ID in route');
            this.snackbarService.errorSnackBar('No Id');
            this.isPaymentRequestDataLoading.set(false);
            return;
        }

        try {
            this.paymentData = await this.requestService.getPaymentRequestById(id);
            this.isPaymentRequestDataLoading.set(false);
        } catch {
            this.snackbarService.errorSnackBar('Failed to load payment request.');
            this.isPaymentRequestDataLoading.set(false);
            return;
        }

        await this.resolveCreatorPartnerApprove();
    }

    private async resolveCreatorPartnerApprove(): Promise<void> {
        if (this.paymentData.createdByRole) {
            try {
                const res: UserRole = await this.manageUserRoleService.getUserPermissionsByRole(String(this.paymentData.createdByRole));
                this.paymentData.isCreatorPartnerApprove = res.partnerApprove;
                if (this.paymentData.isCreatorPartnerApprove && this.paymentData.previousStages) {
                    this.paymentData.previousStages = this.paymentData.previousStages.filter(s => s.stageOrder !== 2);
                }
            } catch {
                this.paymentData.isCreatorPartnerApprove = false;
            }
        } else {
            this.paymentData.isCreatorPartnerApprove = false;
        }
    }

    private async resolveActionstepLinks(): Promise<void> {
        try {
            this.actionstepBaseUrl = await this.requestService.getActionstepUrl();
            this.actionstepOverviewBaseUrl = await this.requestService.getActionStepOverviewUrl();

            if (this.paymentData?.matterNo) {
                this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.paymentData.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
                this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.paymentData.matterNo}`;
                this.showLinkToOpenMatter.set(true);
                this.matterNo = this.paymentData.matterNo;
            }
        } catch {
            console.warn('Failed to resolve Actionstep links. Continuing without them.');
        }
    }

    private initializeStageFlags(): void {
        const stageId = this.paymentData?.currentStage?.stageOrder ?? 0;
        this.canEditFields = stageId === 3;
        this.accountsEditableFields = stageId === 4;
        this.isAmountReleased = stageId === 6 || stageId === 7;
        this.isAmountReleasedDisabled = stageId === 7;
        this.isDisabled = stageId !== 3;
        this.isStageAccounts1 = stageId === 4;
        this.isStagePartner2 = stageId >= 5;
    }

    private async loadSupportingData(): Promise<void> {
        if (!this.paymentData) return;

        const stageId = this.paymentData.currentStage?.stageOrder;

        try {
            this.bankAccounts = await this.bankAccountService.getAllBankAccounts();
            this.filteredBankAccounts = this.bankAccounts;
        } catch {
            console.warn('Failed to load bank accounts.');
        }

        this.approveBtn = this.paymentData.nextStage?.stageName || '';
        this.previousWorkflows = this.paymentData.previousStages?.filter(x => x.stageOrder !== 1) ?? [];

        try {
            const audits = await this.auditService.getUserAuditByRequest(this.paymentData.id, RequestTypeOrder[RequestType.Payment]);
            const latestAudit = audits?.length ? audits[audits.length - 1] : null;
            this.isLastAuditRpaSuccessful = latestAudit?.rpaStatus === RpaStatus.Successful;
            this.isRejectionRequested = (latestAudit?.rpaStatus === RpaStatus.Successful && latestAudit.isRejectionRequested === true) ?? false;
            this.isStagePartner2Return = stageId === 5 && this.isLastAuditRpaSuccessful;
            this.isApproveBlockedByRpaFailure = this.paymentData.currentStage?.isRpaStage === true
                && latestAudit?.rpaStatus === RpaStatus.Failed;
        } catch {
            console.warn('Failed to load audits.');
        }

        this.typeOfPayment = this.paymentData.paymentType ?? '';
        this.isInternational = this.typeOfPayment === 'International';

        try {
            this.purchaseCodes = await this.requestService.getPurchaseCodeLookup();
        } catch {
            console.warn('Failed to load purchase codes.');
        }

        try {
            this.partnerList = await this.requestService.getPartnersList();
        } catch {
            console.warn('Failed to load partners list.');
        }

        this.filterBankAccounts();

        if (this.isInternational) {
            try {
                this.currencyDropdown = await this.requestService.getAllCurrencies();
            } catch {
                console.warn('Failed to load currencies.');
            }
        }

        if (stageId === 7) {
            this.disableButtons = true;
        }
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    private patchFormValues(): void {
        if (!this.paymentData) return;

        const stageId = this.paymentData.currentStage?.stageOrder;

        this.paymentRequestForm.patchValue({
            paymentType: this.isInternational ? 'International' : 'Domestic',
            returnReason: this.paymentData.returnReason,
            matterNumber: this.paymentData.matterNo,
            matter: this.paymentData.matter,
            matterName: `${this.paymentData.matterNo ?? ''} - ${this.paymentData.matter ?? ''}`,
            dateTransaction: this.paymentData.dateTransaction ? new Date(this.paymentData.dateTransaction) : null,
            reason: this.paymentData.reason,
            returnMoniesExplanationIfNo: this.paymentData.returnMoniesExplanationIfNo,
            otherInfo: this.paymentData.otherInformation,
            address: this.paymentData.address ?? '',
            amount: this.paymentData.amount,
            vatAmount: this.paymentData.vatAmount,
            currency: this.currencyDropdown.find(c => c.id === this.paymentData.currencyId)?.currencyCode ?? '',
            name: this.paymentData.payee,
            sortCode: this.paymentData.sortCode?.replace(/\D/g, ''),
            accountNo: this.paymentData.accountNumber,
            bankName: this.paymentData.bankName,
            reference: this.paymentData.payeeReference,
            transactionReference: this.paymentData.transactionReference,
            finalBeneficiaryAccNo: this.paymentData.beneficiaryAccountNumber,
            finalBeneficiarySwift: this.paymentData.swiftBicCode,
            finalReference: this.paymentData.reference,
            intermAccountNo: this.paymentData.intermediaryAccountNumber,
            intermSwift: this.paymentData.intermediarySwiftBicCode,
            intermBankName: this.paymentData.intermediaryBankName,
            interReference: this.paymentData.intermediaryReference,
            isMoniesReturned: this.paymentData.moniesReturnedClient,
            isAccountDetailsVerifies: this.paymentData.accountTelephoneVerification === true ? 'Yes'
                : this.paymentData.accountTelephoneVerification === false ? 'No' : '',
            telephoneVerificationDate: this.paymentData.telephoneVerificationDate
                ? new Date(this.paymentData.telephoneVerificationDate) : null,
            verificationSpokeTo: this.paymentData.verificationSpokeTo,
            verificationNoReason: this.paymentData.verificationNoReason,
            chapsRequired: this.paymentData.chapsRequired,
            dateAuthorized: this.paymentData.dateAuthorised ? new Date(this.paymentData.dateAuthorised) : null,
            dateRequested: this.paymentData.dateRequested,
            clientBankAccount: this.resolveBankAccount(this.paymentData.clientBankAccountId),
            fundsCheckedByPartner: this.paymentData.fundsCheckedByPartner ?? false,
            fundsCheckedByAccounts: this.paymentData.fundsCheckedByAccounts ?? false,
            isCheckedLedger: this.paymentData.isLedgerChecked ?? false,
            matterPartner: this.paymentData.matterPartner,
            nonPartnerReason: this.paymentData.nonPartnerReason,
            isPartnerCertified: this.paymentData.isPartnerCertified,
            confirmPayeeName: this.paymentData.confirmPayeeName,
            noMatchReason: this.paymentData.noMatchReason,
            partnerResponse: this.paymentData.partnerResponse,
            partnerAuthorisingId: this.paymentData.partnerAuthorisingId ?? null,
        });

        if (this.paymentData.dateRequested) {
            const d = new Date(this.paymentData.dateRequested);
            this.paymentRequestForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

        if (this.paymentData.dateTransaction) {
            const d = new Date(this.paymentData.dateTransaction);
            this.paymentRequestForm.get('transactionTime')?.setValue(this.toTimeString(d));
        }

        if (this.isFileUploadRequired) {
            this.paymentRequestForm.get('fileUpload')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('fileUpload')?.updateValueAndValidity();
        }

        if (this.paymentData.chapsRequired) {
            this.paymentRequestForm.patchValue({
                isPropertyPurchase: this.paymentData.isPropertyPurchase,
                purchaseCode: this.paymentData.purchaseCode?.code ?? null
            });
        }

        if (this.paymentData?.dateRequested) {
            this.paymentRequestForm.get('dateRequested')?.disable();
            this.paymentRequestForm.get('requestedTime')?.disable();
        }

        this.handleIsMoniesReturned(this.paymentRequestForm.get('isMoniesReturned')?.value, true);

        if (stageId === 4) {
            this.paymentRequestForm.get('dateTransaction')?.setValue(new Date());
            this.paymentRequestForm.get('transactionTime')?.setValue(this.toTimeString(new Date()));
        }

        this.showReturnReason = !!this.paymentData.returnReason;
    }

    private applyFieldStates(): void {
        if (!this.paymentData) return;

        const stageId = this.paymentData.currentStage?.stageOrder ?? 0;
        const canEditAny = this.canEditFields || this.accountsEditableFields;

        this.paymentRequestForm.get('matterName')?.disable();
        this.paymentRequestForm.get('partnerAuthorisingId')?.disable();

        if (this.paymentData.dateAuthorised) {
            this.paymentRequestForm.get('dateAuthorized')?.disable();
        }

        if (this.isAmountReleasedDisabled) {
            this.paymentRequestForm.get('isCheckedLedger')?.disable();
        }

        if (!this.canEditFields) {
            [
                'otherInfo', 'currency', 'amount', 'vatAmount', 'name', 'reference',
                'finalBeneficiaryAccNo', 'finalBeneficiarySwift', 'finalReference',
                'intermAccountNo', 'intermSwift', 'interReference', 'purchaseCode',
                'sortCode', 'accountNo', 'isMoniesReturned',
                'isPropertyPurchase', 'isAccountDetailsVerifies', 'telephoneVerificationDate',
                'verificationSpokeTo', 'verificationNoReason', 'reason',
                'returnMoniesExplanationIfNo', 'matterPartner', 'nonPartnerReason',
                'fundsCheckedByPartner', 'address',
            ].forEach(f => this.paymentRequestForm.get(f)?.disable());
        }

        if (!canEditAny) {
            ['clientBankAccount', 'bankName', 'intermBankName', 'chapsRequired']
                .forEach(f => this.paymentRequestForm.get(f)?.disable());
        }

        if (stageId === 4) {
            this.paymentRequestForm.get('transactionReference')?.enable();
            this.paymentRequestForm.get('confirmPayeeName')?.enable();
            this.paymentRequestForm.get('transactionTime')?.enable();
            this.paymentRequestForm.get('dateTransaction')?.enable();
        } else {
            this.paymentRequestForm.get('transactionReference')?.disable();
            this.paymentRequestForm.get('confirmPayeeName')?.disable();
            this.paymentRequestForm.get('transactionTime')?.disable();
            this.paymentRequestForm.get('dateTransaction')?.disable();
        }

        if (stageId >= 5) {
            this.paymentRequestForm.get('noMatchReason')?.disable();
        }

        if (stageId >= 6) {
            this.paymentRequestForm.get('partnerResponse')?.disable();
        }

        if (!this.paymentData.chapsRequired) {
            if (!this.canEditFields && !this.accountsEditableFields) {
                this.paymentRequestForm.get('isPropertyPurchase')?.disable();
                this.paymentRequestForm.get('purchaseCode')?.disable();
            }
        } else if (this.canEditFields || this.accountsEditableFields) {
            this.paymentRequestForm.get('isPropertyPurchase')?.enable();
            if (this.paymentData.isPropertyPurchase) {
                this.paymentRequestForm.get('purchaseCode')?.enable();
            }
        }

        if (this.paymentRequestForm.get('uploadedFiles')?.value.length === 0) {
            this.disableUpload = true;
        }
    }

    private applyStageValidators(): void {
        if (!this.paymentData) return;

        const stageId = this.paymentData.currentStage?.stageOrder ?? 0;
        const partnerAuthorisingControl = this.paymentRequestForm.get('partnerAuthorisingId');
        const uploadedFilesControl = this.paymentRequestForm.get('uploadedFiles');
        if (stageId < 4) {
            this.paymentRequestForm.get('dateTransaction')?.clearValidators();
            this.paymentRequestForm.get('dateTransaction')?.updateValueAndValidity();
        }

        if (stageId === 4) {
            this.paymentRequestForm.get('transactionReference')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('transactionReference')?.updateValueAndValidity();
            this.paymentRequestForm.get('confirmPayeeName')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('confirmPayeeName')?.updateValueAndValidity();
            partnerAuthorisingControl?.enable();
            uploadedFilesControl?.setValidators([(control) => {
                return control.value && control.value.length > 0 ? null : { required: true };
            }]);
            uploadedFilesControl?.updateValueAndValidity();
        } else {
            this.paymentRequestForm.get('transactionReference')?.clearValidators();
            this.paymentRequestForm.get('transactionReference')?.updateValueAndValidity();
            this.paymentRequestForm.get('confirmPayeeName')?.clearValidators();
            this.paymentRequestForm.get('confirmPayeeName')?.updateValueAndValidity();
            partnerAuthorisingControl?.disable();
            uploadedFilesControl?.clearValidators();
            uploadedFilesControl?.updateValueAndValidity();
        }

        if (stageId >= 5) {
            this.paymentRequestForm.get('noMatchReason')?.clearValidators();
            this.paymentRequestForm.get('noMatchReason')?.updateValueAndValidity();
        }

        const isCheckedLedgerControl = this.paymentRequestForm.get('isCheckedLedger');
        if (this.isAmountReleased && !this.isAmountReleasedDisabled) {
            isCheckedLedgerControl?.setValidators([Validators.requiredTrue]);
        } else {
            isCheckedLedgerControl?.clearValidators();
        }
        isCheckedLedgerControl?.updateValueAndValidity();

        const isVerified = this.paymentRequestForm.get('isAccountDetailsVerifies')?.value;
        const verificationNoReasonControl = this.paymentRequestForm.get('verificationNoReason');
        if (isVerified === 'No') {
            verificationNoReasonControl?.setValidators([Validators.required]);
        } else {
            verificationNoReasonControl?.clearValidators();
        }
        verificationNoReasonControl?.updateValueAndValidity();

        if (this.isInternational) {
            this.paymentRequestForm.get('sortCode')?.clearValidators();
            this.paymentRequestForm.get('sortCode')?.updateValueAndValidity();
            this.paymentRequestForm.get('accountNo')?.clearValidators();
            this.paymentRequestForm.get('accountNo')?.updateValueAndValidity();
            this.paymentRequestForm.get('currency')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('vatAmount')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('finalBeneficiaryAccNo')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('finalBeneficiarySwift')?.setValidators([Validators.required]);
            this.paymentRequestForm.get('finalReference')?.setValidators([Validators.required]);
        } else {
            this.paymentRequestForm.get('sortCode')?.setValidators([
                Validators.required, Validators.maxLength(6),
                Validators.pattern(/^\d{6}$/)
            ]);
            this.paymentRequestForm.get('sortCode')?.updateValueAndValidity();
            this.paymentRequestForm.get('accountNo')?.setValidators([
                Validators.required, Validators.maxLength(8),
                Validators.minLength(8), Validators.pattern(/^\d{8}$/)
            ]);
            this.paymentRequestForm.get('accountNo')?.updateValueAndValidity();
        }

        const isChaps = this.paymentRequestForm.get('chapsRequired')?.value === true;
        const addressCtrl = this.paymentRequestForm.get('address');
        addressCtrl?.clearValidators();
        if (this.isInternational) {
            addressCtrl?.setValidators([Validators.required]);
        } else if (!isChaps) {
            addressCtrl?.setValue('', { emitEvent: false });
        }
        addressCtrl?.updateValueAndValidity();

        this.showFundsChecked();
    }

    private setupValueChangeListeners(): void {
        const stageId = this.paymentData?.currentStage?.stageOrder ?? 0;

        const initialAccount = this.paymentRequestForm.get('clientBankAccount')?.value as BankAccount | null;
        this.selectedBankAccountId = initialAccount?.accountId ?? '';

        if (this.paymentData?.matterNo) {
            this.matterNo = this.paymentData.matterNo;
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.paymentRequestForm.get('clientBankAccount')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((account: BankAccount | null) => {
                this.selectedBankAccountId = account?.accountId ?? '';

                if (this.matterNo) {
                    this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
                }
            });

        this.paymentRequestForm.get('matterNumber')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((matterNo: number) => {
                this.matterNo = matterNo;

                this.actionstepMatterUrl =
                    `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;

                this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            });

        this.paymentRequestForm.get('isMoniesReturned')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => this.handleIsMoniesReturned(val));

        this.paymentRequestForm.get('confirmPayeeName')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                const noMatchReasonCtrl = this.paymentRequestForm.get('noMatchReason');
                const partnerResponseCtrl = this.paymentRequestForm.get('partnerResponse');

                if (stageId >= 5 && val === false) {
                    partnerResponseCtrl?.setValidators([Validators.required]);
                    partnerResponseCtrl?.updateValueAndValidity();
                    return;
                }

                if (val === false) {
                    noMatchReasonCtrl?.enable();
                    noMatchReasonCtrl?.setValidators([Validators.required]);
                } else {
                    noMatchReasonCtrl?.reset();
                    noMatchReasonCtrl?.clearValidators();
                    noMatchReasonCtrl?.disable();
                }
                partnerResponseCtrl?.updateValueAndValidity();
                noMatchReasonCtrl?.updateValueAndValidity();
            });

        this.paymentRequestForm.get('isAccountDetailsVerifies')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                if (val === 'Yes') {
                    this.paymentRequestForm.get('telephoneVerificationDate')?.setValidators([Validators.required]);
                    this.paymentRequestForm.get('verificationSpokeTo')?.setValidators([Validators.required]);
                    this.paymentRequestForm.get('verificationNoReason')?.clearValidators();
                } else if (val === 'No') {
                    this.paymentRequestForm.get('verificationNoReason')?.setValidators([Validators.required]);
                    this.paymentRequestForm.get('telephoneVerificationDate')?.clearValidators();
                    this.paymentRequestForm.get('verificationSpokeTo')?.clearValidators();
                }
                this.paymentRequestForm.get('telephoneVerificationDate')?.updateValueAndValidity();
                this.paymentRequestForm.get('verificationSpokeTo')?.updateValueAndValidity();
                this.paymentRequestForm.get('verificationNoReason')?.updateValueAndValidity();
            });

        this.paymentRequestForm.get('chapsRequired')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                if (!this.canEditFields && !this.accountsEditableFields) return;
                if (val) {
                    this.paymentRequestForm.get('isPropertyPurchase')?.enable();
                    if (this.paymentRequestForm.get('isPropertyPurchase')?.value) {
                        this.paymentRequestForm.get('purchaseCode')?.enable();
                    }
                } else {
                    this.paymentRequestForm.patchValue({ isPropertyPurchase: false, purchaseCode: null });
                    this.paymentRequestForm.get('isPropertyPurchase')?.disable();
                    this.paymentRequestForm.get('purchaseCode')?.disable();
                }

                const addressCtrl = this.paymentRequestForm.get('address');
                addressCtrl?.clearValidators();
                if (this.isInternational) {
                    addressCtrl?.setValidators([Validators.required]);
                } else if (!val) {
                    addressCtrl?.setValue('', { emitEvent: false });
                }
                addressCtrl?.updateValueAndValidity();
            });

        this.paymentRequestForm.get('isPropertyPurchase')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                if (!this.canEditFields && !this.accountsEditableFields) return;
                if (val) {
                    this.paymentRequestForm.get('purchaseCode')?.enable();
                    this.paymentRequestForm.get('purchaseCode')?.setValidators([Validators.required]);
                } else {
                    this.paymentRequestForm.get('purchaseCode')?.disable();
                    this.paymentRequestForm.patchValue({ purchaseCode: null });
                }
            });

        this.paymentRequestForm.get('matterPartner')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
                if (!this.canEditFields) return;
                if (val) {
                    this.paymentRequestForm.get('nonPartnerReason')?.disable();
                    this.paymentRequestForm.get('nonPartnerReason')?.clearValidators();
                    this.paymentRequestForm.patchValue({ nonPartnerReason: null });
                } else {
                    this.paymentRequestForm.get('nonPartnerReason')?.enable();
                    this.paymentRequestForm.get('nonPartnerReason')?.setValidators([Validators.required]);
                    this.paymentRequestForm.get('nonPartnerReason')?.setValue(this.paymentData.nonPartnerReason);
                }
                this.paymentRequestForm.get('nonPartnerReason')?.updateValueAndValidity();
            });

        this.paymentRequestForm.get('confirm')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => console.log(value));
    }

    private setupStorageListener(): void {
        fromEvent<StorageEvent>(window, 'storage')
            .pipe(
                filter(e => e.key === 'paymentExpandedClosed'),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.closeDialog());
    }

    /** Loads the matter's clients, so the payee can be ticked from them when there are several. */
    private async loadClientOptions(): Promise<void> {
        if (!this.paymentData?.matterNo) return;
        try {
            this.clientOptions = await this.matterService.getMatterClientNames(Number(this.paymentData.matterNo));
        } catch {
            this.clientOptions = [];
        }
    }

    /** True when the approver can edit the payee and the matter has several clients to tick it from. */
    get showPayeeSelect(): boolean {
        return this.canEditFields && this.clientOptions.length > 1
            && this.paymentRequestForm.get('isMoniesReturned')?.value === true
            && this.payeeMatchesClients();
    }

    /**
     * True when the text in the field is empty or is exactly the ticked clients. Text saved before names were shown
     * as "First Last" (or typed by hand) is not, so it is shown as plain text rather than as a blank dropdown.
     */
    private payeeMatchesClients(): boolean {
        const text = this.paymentRequestForm.get('name')?.value ?? '';
        const ticked = this.payeeClients.value;
        return !text || text === this.clientOptions.filter(name => ticked.includes(name)).join(', ');
    }

    /** Sets the text to the ticked clients, in the order they are listed. */
    onPayeeClientsChange(): void {
        const ticked = this.payeeClients.value;
        this.paymentRequestForm.get('name')?.setValue(this.clientOptions.filter(name => ticked.includes(name)).join(', '));
    }

    onPayeeSelectOpened(isOpen: boolean): void {
        if (!isOpen) this.paymentRequestForm.get('name')?.markAsTouched();
    }

    /** Ticks the listed clients that appear in the current text. */
    private syncPayeeClients(): void {
        const payeeControl = this.paymentRequestForm.get('name');
        const padded = `, ${payeeControl?.value ?? ''}, `;
        this.payeeClients.setValue(this.clientOptions.filter(name => padded.includes(`, ${name}, `)));
    }

    /**
     * @param isInitial True when the request is first shown: the saved payee is kept, so the clients ticked when the
     * request was made stay ticked.
     */
    private handleIsMoniesReturned(val: boolean, isInitial = false): void {
        const reasonControl = this.paymentRequestForm.get('reason');
        const explanationCtrl = this.paymentRequestForm.get('returnMoniesExplanationIfNo');

        reasonControl?.setValue(this.paymentData.reason);
        reasonControl?.setValidators([Validators.required]);
        reasonControl?.updateValueAndValidity();

        if (val === true) {
            explanationCtrl?.clearValidators();
            if (!isInitial) {
                // With several clients the approver ticks which of them is the payee.
                this.paymentRequestForm.get('name')?.setValue(this.clientOptions.length > 1 ? '' : (this.clientOptions[0] ?? ''));
            }
        } else {
            explanationCtrl?.setValue(this.paymentData.returnMoniesExplanationIfNo);
            explanationCtrl?.setValidators([Validators.required]);
            this.paymentRequestForm.get('name')?.setValue(this.paymentData.payee);
        }
        this.syncPayeeClients();
        explanationCtrl?.updateValueAndValidity();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {

            if (this.isDialogOpen()) {

                const contentEl = this.dialogContent.nativeElement as HTMLElement;

                const currentHeight = contentEl.offsetHeight;

                contentEl.style.height = `${currentHeight}px`;

                contentEl.style.overflowY = 'auto';
            }
        }, 50);
    }

    showFundsChecked() {
        const stageId = this.paymentData?.currentStage?.stageOrder;

        this.showFundsCheckedByPartner = stageId === 3;
        this.FundsCheckedPartnerText = stageId === 4 || stageId === 5 || stageId === 6;
        this.showFundsCheckedByAccounts = stageId === 4;
        this.FundsCheckedAccountsText = stageId === 5 || stageId === 6;

        if (stageId === 3) {
            this.paymentRequestForm.get('fundsCheckedByPartner')?.setValidators([Validators.requiredTrue]);
            this.paymentRequestForm.get('fundsCheckedByAccounts')?.clearValidators();
            this.paymentRequestForm.get('isPartnerCertified')?.setValidators([Validators.requiredTrue]);
        }

        if (stageId === 4) {
            this.paymentRequestForm.get('fundsCheckedByPartner')?.setValidators([Validators.requiredTrue]);
            this.paymentRequestForm.get('fundsCheckedByAccounts')?.setValidators([Validators.requiredTrue]);
            this.paymentRequestForm.get('isPartnerCertified')?.clearValidators();
            this.paymentRequestForm.get('isPartnerCertified')?.disable();
        }

        if (stageId === 5 || stageId === 6) {
            this.paymentRequestForm.get('fundsCheckedByAccounts')?.disable();
        }

        this.paymentRequestForm.get('fundsCheckedByPartner')?.updateValueAndValidity();
        this.paymentRequestForm.get('fundsCheckedByAccounts')?.updateValueAndValidity();
    }

    get isTransactionRefVisible(): boolean {
        const stageId = this.paymentData?.currentStage?.stageOrder ?? 0;
        return stageId >= 4;
    }

    get isDateTransactionVisible(): boolean {
        return (this.paymentData?.currentStage?.stageOrder ?? 0) >= 4;
    }

    get isFileUploadRequired(): boolean {
        return this.paymentData?.currentStage?.stageOrder === 4;
    }

    get getRequestId(): number | undefined {
        return this.paymentData?.id;
    }

    get hasAttachments(): boolean {
        return this.paymentData?.documentAvailable ?? false;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event): void {
        this.unlockPopup();
    }

    unlockPopup() {
        this.requestService.requestLockorUnLock(this.paymentData.id, RequestType.Payment).then((res) => {
            if (res) {
                this.snackbarService.successSnackBar("The request has been unlocked.");
            }
        })
    }

    uploadDoc() {
        if (this.paymentRequestForm.get('uploadedFiles')?.value.length > 0) {
            const docData: RequestDocAttachmentResponse = {
                requestId: this.paymentData.id,
                requestType: RequestType.Payment,
                attachments: this.paymentRequestForm.get('uploadedFiles')?.value
            }

            this.requestService.addRequestDocument(docData).then((res) => {
                this.snackbarService.successSnackBar(`${res}`);
            })
        }

        if (this.dialogRef) {
            this.dialogRef.disableClose = true;
        }

        if (this.dialogRef) {
            this.dialogRef.close(true);
        }
    }

    onFilesSelected(input: any) {
        if (input.files) {
            this.fileNames = [];
            this.displayFile = [];
            const filesArray = Array.from(input.files);
            let loadedCount = 0;

            filesArray.forEach((file: any) => {
                const fileName = file.name;
                this.displayFile.push(fileName);
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const bytes = e.target.result.split('base64,')[1];
                    this.fileNames.push({ fileName: fileName, attachments: bytes });
                    loadedCount++;
                    if (loadedCount === filesArray.length) {
                        this.paymentRequestForm.get('uploadedFiles')?.setValue(this.fileNames);
                    }
                }
                reader.readAsDataURL(file);
            });

            this.paymentRequestForm.get('fileUpload')?.setValue(this.displayFile.length ? this.displayFile : null);
            this.paymentRequestForm.get('fileUpload')?.markAsTouched();
        }
        this.paymentRequestForm.get('fileUpload')?.setValue(this.displayFile.length ? this.displayFile : null);
        this.paymentRequestForm.get('fileUpload')?.markAsTouched();
    }

    clearUpload(fileInput: any) {
        this.displayFile = [];
        this.fileNames = [];
        this.paymentRequestForm.get('uploadedFiles')?.setValue([]);
        fileInput.value = '';
        this.paymentRequestForm.get('fileUpload')?.setValue(null);
        this.paymentRequestForm.get('fileUpload')?.markAsTouched();
    }

    isReturnDisabled(stageId: number): boolean {

        if (!this.isLastAuditRpaSuccessful) {
            return false;
        }

        const currentStageId = this.paymentData?.currentStage?.stageOrder;

        if (currentStageId === 4 || currentStageId === 6) {
            return false;
        }

        return stageId !== 4 && stageId !== 6;
    }


    async returnRequest(stageId: number, stageName: string) {
        const audits = await this.auditService.getUserAuditByRequest(this.paymentData.id, RequestTypeOrder[RequestType.Payment]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.rpaStatus === RpaStatus.Successful;
        const dialogRef = this.dialog.open(ReturnReasonDialogComponent, {
            width: '600px',
            data: {
                stageName,
                matterNo: this.paymentData?.matterNo,
                currentStageId: this.paymentData?.currentStage?.stageOrder,
                rpaStatus: rpaStatus,
                selectedBankAccountId: this.selectedBankAccountId,
            },
            disableClose: true
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result) {
                    this.requestService.returnPaymentRequest(
                        this.paymentData.id,
                        stageId,
                        result.reason,
                        result.isRPAReversalRequested,
                        result.isRejectionRequested
                    )
                        .then(res => {
                            this.snackbarService.successSnackBar(res);
                            this.closeDialog();
                        })
                }
            });
    }

    async delete() {

        const audits = await this.auditService.getUserAuditByRequest(this.paymentData.id, RequestTypeOrder[RequestType.Payment]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.rpaStatus === RpaStatus.Successful;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Are you sure?',
                message: 'The data associated with the request will be deleted.',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.paymentData?.matterNo,
                currentStageId: this.paymentData?.currentStage?.stageOrder,
                rpaStatus: rpaStatus,
                selectedBankAccountId: this.selectedBankAccountId,
            }
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(res => {
                if (res) {
                    this.requestService.deletePaymentRequest(this.paymentData.id)
                        .then((res) => {
                            if (res) {
                                this.snackbarService.successSnackBar(`${res}`);
                                this.closeDialog();
                            }
                        })
                        .catch((error) => {
                            this.snackbarService.errorSnackBar("Failed to delete payment request. Please try again.");
                        });
                }
            })
    }

    allowOnlyDigits(event: KeyboardEvent): void {
        if (!/^[0-9]$/.test(event.key)) {
            event.preventDefault();
        }
    }

    sanitizeSortCode(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digits = input.value.replace(/\D/g, '').slice(0, 6);
        if (digits !== input.value) {
            input.value = digits;
            this.paymentRequestForm.get('sortCode')?.setValue(digits);
        }
    }

    closeDialog() {
        if (this.dialogRef) {
            this.dialogRef.close(true);
        }

        if (!this.isDialogOpen()) {
            window.close();
        }
    }

    async approveRequest() {
        const latestData = await this.requestService.getPaymentRequestById(this.paymentData.id);

        const selectedAccount = this.paymentRequestForm.get('clientBankAccount')?.value as BankAccount | null;

        const approvalDetails: ApprovalDetails = {
            dateTransaction: (() => {
                const datePart = this.paymentRequestForm.get('dateTransaction')?.value;
                if (!datePart) return undefined;

                const d = datePart instanceof Date ? datePart : new Date(datePart);
                const [hours, minutes] = (
                    this.paymentRequestForm.get('transactionTime')?.value ?? '00:00'
                ).split(':').map(Number);

                return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, 0, 0);
            })(),
            reasonForPayment: this.paymentRequestForm.value.reason || this.paymentData.reason,
            returnMoniesExplanationIfNo: this.paymentRequestForm.value.returnMoniesExplanationIfNo || this.paymentData.returnMoniesExplanationIfNo,
            bankName: this.paymentRequestForm.value.bankName || this.paymentData.bankName,
            sortCode: this.paymentRequestForm.value.sortCode || this.paymentData.sortCode,
            accountNumber: this.paymentRequestForm.value.accountNo || this.paymentData.accountNumber,
            fundsCheckedByPartner: this.paymentRequestForm.value.fundsCheckedByPartner || this.paymentData.fundsCheckedByPartner,
            fundsCheckedByAccounts: this.paymentRequestForm.value.fundsCheckedByAccounts || this.paymentData.fundsCheckedByAccounts,
            accountFundsCheckedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            accountTelephoneVerification: this.paymentRequestForm.value.isAccountDetailsVerifies !== undefined ? this.paymentRequestForm.value.isAccountDetailsVerifies === 'Yes' : this.paymentData.accountTelephoneVerification,
            telephoneVerificationDate: this.paymentRequestForm.value.isAccountDetailsVerifies === 'Yes' ? this.paymentRequestForm.value.telephoneVerificationDate : this.paymentRequestForm.value.isAccountDetailsVerifies === 'No' ? null : this.paymentData.telephoneVerificationDate,
            verificationSpokeTo: this.paymentRequestForm.get('isAccountDetailsVerifies')?.value === 'Yes' ? this.paymentRequestForm.get('verificationSpokeTo')?.value : null,
            verificationNoReason: this.paymentRequestForm.get('isAccountDetailsVerifies')?.value === 'No' ? this.paymentRequestForm.get('verificationNoReason')?.value : null,
            chapsRequired: this.paymentRequestForm.get('chapsRequired')?.value,
            isPropertyPurchase: this.paymentRequestForm.get('isPropertyPurchase')?.value,
            purchaseCode: this.paymentRequestForm.get('purchaseCode')?.value,
            isLedgerChecked: this.paymentRequestForm.value.isCheckedLedger,
            ledgerCheckedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            isPartnerCertified: this.paymentRequestForm.value.isPartnerCertified,
            partnerCertifiedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            paymentMethod: this.paymentRequestForm.get('chapsRequired')?.value ? 'Chaps' : 'Faster Pay',
            isMoniesReturned: this.paymentRequestForm.get('isMoniesReturned')?.value,
            payee: this.paymentRequestForm.get('name')?.value,
            otherInfo: this.paymentRequestForm.get('otherInfo')?.value,
            address: this.paymentRequestForm.get('address')?.value,
            transactionReference: this.paymentRequestForm.get('transactionReference')?.value,
            amount: this.paymentRequestForm.get('amount')?.value,
            vatAmount: this.paymentRequestForm.get('vatAmount')?.value,
            reference: this.paymentRequestForm.get('reference')?.value,
            currency: this.paymentRequestForm.get('currency')?.value,
            finalBeneficiaryAccNo: this.paymentRequestForm.get('finalBeneficiaryAccNo')?.value,
            finalBeneficiarySwift: this.paymentRequestForm.get('finalBeneficiarySwift')?.value,
            finalReference: this.paymentRequestForm.get('finalReference')?.value,
            intermAccountNo: this.paymentRequestForm.get('intermAccountNo')?.value,
            intermSwift: this.paymentRequestForm.get('intermSwift')?.value,
            intermBankName: this.paymentRequestForm.get('intermBankName')?.value,
            interReference: this.paymentRequestForm.get('interReference')?.value,
            matterPartner: this.paymentRequestForm.get('matterPartner')?.value,
            nonPartnerReason: this.paymentRequestForm.get('nonPartnerReason')?.value,
            returnReason: undefined,
            matter: this.paymentRequestForm.get('matter')?.value,
            comments: this.paymentRequestForm.value.comments,
            confirmPayeeName: this.paymentRequestForm.get('confirmPayeeName')?.value ?? undefined,
            noMatchReason: this.paymentRequestForm.get('noMatchReason')?.value,
            partnerResponse: this.paymentRequestForm.get('partnerResponse')?.value,
            partnerAuthorisingId: this.paymentRequestForm.get('partnerAuthorisingId')?.value ?? this.paymentData.partnerAuthorisingId,
            clientBankAccountName: selectedAccount
                ? `${selectedAccount.accountId} - ${selectedAccount.description}`
                : this.paymentData.clientBankAccountName,
            clientBankAccountId: selectedAccount?.accountId ?? this.paymentData.clientBankAccountId,
        };

        this.requestService.approveRequest(
            this.paymentData.id,
            RequestType.Payment,
            this.paymentData.nextStage?.id ?? 0,
            approvalDetails
        )
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.uploadDoc();
                    this.closeDialog();
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve payment request. Please try again.");
            });

    }

    filterBankAccounts() {
        this.filteredBankAccounts = this.bankAccounts;
    }

    downloadDocuments(requestId: number, requestType: string) {
        this.requestService.getRequestDocumentList(requestId, requestType).then((res) => {
            this.fileToBeDownloaded = res;
            this.downloadMultipleBase64Files(this.fileToBeDownloaded.attachments ?? []);
        });
    }

    downloadMultipleBase64Files(files: AttachmentData[]): void {
        files.forEach(file => {
            const { attachments, fileName } = file;
            const byteCharacters = atob(attachments);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    }

    @HostListener('window:beforeunload')
    onExpandedTabClose() {
        localStorage.setItem('paymentExpandedClosed', Date.now().toString());
    }

    openInNewTab() {
        const id = this.paymentData?.id;
        if (!id) return;

        localStorage.setItem('paymentExpandedOpen', Date.now().toString());

        const url = this.router.serializeUrl(
            this.router.createUrlTree([
                '/approvals',
                'payment-request',
                id
            ])
        );
        window.open(window.location.origin + url, '_blank');
    }

    onUrlCopied(): void {
        this.snackbarService.successSnackBar('URL copied to clipboard');
    }
}
